export interface ColorsOption {
  original: { r: number; g: number; b: number }[];
  current: { r: number; g: number; b: number }[];
}

// per-channel distance to a palette color for a pixel to be recolored with it (legacy matching)
const BOX_TOL = 128;
// pixels more transparent than this are skipped, they fade to fully transparent anyway
const MIN_ALPHA = 16;
// max per-channel difference to all 8 neighbours for a pixel to count as a flat (non-blended) area
const FLAT_TOL = 24;
// neighbourhood radius used to find the colors a boundary pixel is blending between
const RADIUS = 2;
// max squared chroma distance between a pixel and the best blend before it is treated as unrelated art
const RESID_TOL = 40 * 40;
// score penalty per non-palette anchor, so exact ties prefer blends of palette colors
const TIE_EPS = 16;
// fixed anchors below this squared chroma are covered by the neutral anchor
const NEUTRAL_CHROMA = 12 * 12;
const MAX_FIXED_ANCHORS = 4;
// alpha below this is faded out, as semi transparent pixels are mostly unused in the templates
const ALPHA_FADE = 100;

const LABEL_NONE = -1;
// alpha below MIN_ALPHA, these end up fully transparent after the fade
const LABEL_TRANSPARENT = -2;

// palette colors flattened to [r, g, b, r, g, b, ...]
function flattenColors(list: { r: number; g: number; b: number }[]) {
  const flat = new Float64Array(list.length * 3);
  list.forEach((color, c) => flat.set([color.r, color.g, color.b], c * 3));
  return flat;
}

function paletteLabel(r: number, g: number, b: number, original: Float64Array) {
  for (let c = 0; c < original.length; c += 3) {
    if (Math.abs(r - original[c]) <= BOX_TOL && Math.abs(g - original[c + 1]) <= BOX_TOL && Math.abs(b - original[c + 2]) <= BOX_TOL) return c / 3;
  }
  return LABEL_NONE;
}

// previous behaviour: the matched user color, shifted by the average difference to the palette color
function legacyRecolor(data: Uint8ClampedArray, i: number, label: number, original: Float64Array, current: Float64Array) {
  if (label < 0) return;
  const c = label * 3;
  const delta = (data[i] - original[c] + (data[i + 1] - original[c + 1]) + (data[i + 2] - original[c + 2])) / 3;
  data[i] = current[c] + delta;
  data[i + 1] = current[c + 1] + delta;
  data[i + 2] = current[c + 2] + delta;
}

// labels every pixel with the palette color it matches
function labelPixels(data: Uint8ClampedArray, width: number, height: number, original: Float64Array) {
  const labels = new Int8Array(width * height);
  for (let p = 0; p < width * height; p++) {
    const i = p * 4;
    labels[p] = data[i + 3] < MIN_ALPHA ? LABEL_TRANSPARENT : paletteLabel(data[i], data[i + 1], data[i + 2], original);
  }
  return labels;
}

// marks visible pixels next to a visible pixel with a different label
function findEdges(labels: Int8Array, width: number, height: number) {
  const edge = new Uint8Array(width * height);
  for (let y = 0, p = 0; y < height; y++) {
    for (let x = 0; x < width; x++, p++) {
      const label = labels[p];
      if (label === LABEL_TRANSPARENT) continue;
      const left = labels[p - 1];
      if (x > 0 && left !== LABEL_TRANSPARENT && left !== label) edge[p] = edge[p - 1] = 1;
      const up = labels[p - width];
      if (y > 0 && up !== LABEL_TRANSPARENT && up !== label) edge[p] = edge[p - width] = 1;
    }
  }
  return edge;
}

// Palette features thinner than 2k+1 px take the label of the palette area around them. Meant for
// templates that ignore the solid color rule, e.g. stitch lines inside color 1 drawn in a shade that
// falls into the color 2 range, which would otherwise show up as color 2 specks.
function absorbStrayLabels(labels: Int8Array, width: number, height: number, k: number) {
  const n = width * height;
  const span = 2 * k + 1;
  // horizontal pass: the pixel is the middle of a row of `span` pixels with its palette label
  const rowCore = new Uint8Array(n);
  for (let y = 0; y < height; y++) {
    for (let x = 0, run = 0, p = y * width; x < width; x++, p++) {
      if (labels[p] < 0) run = 0;
      else run = x > 0 && labels[p - 1] === labels[p] ? run + 1 : 1;
      if (run >= span) rowCore[p - k] = 1;
    }
  }
  // vertical pass over the horizontal one: the whole span x span window around a core pixel has its label
  const core = new Uint8Array(n);
  const run = new Int32Array(width);
  for (let y = 0, p = 0; y < height; y++) {
    for (let x = 0; x < width; x++, p++) {
      if (!rowCore[p]) run[x] = 0;
      else run[x] = y > 0 && rowCore[p - width] && labels[p - width] === labels[p] ? run[x] + 1 : 1;
      if (run[x] >= span) core[p - k * width] = 1;
    }
  }
  // every pixel in a core window shares the core label, so the pixels covered by one are part of a real area
  const supported = dilate(core, width, height, k);

  const counts = new Int32Array(8);
  const r = k + 1;
  for (let y = 0, p = 0; y < height; y++) {
    for (let x = 0; x < width; x++, p++) {
      if (labels[p] < 0 || supported[p]) continue;
      counts.fill(0);
      for (let yy = Math.max(0, y - r); yy <= Math.min(height - 1, y + r); yy++) {
        for (let xx = Math.max(0, x - r), q = yy * width + xx; xx <= Math.min(width - 1, x + r); xx++, q++) {
          if (supported[q] && labels[q] >= 0) counts[labels[q]]++;
        }
      }
      let best = -1;
      for (let c = 0; c < counts.length; c++) if (counts[c] > 0 && (best < 0 || counts[c] > counts[best])) best = c;
      // only supported pixels are read above, and those never change, so relabeling in place is safe
      if (best >= 0) labels[p] = best;
    }
  }
}

// marks pixels within `radius` of a marked pixel
function dilate(edge: Uint8Array, width: number, height: number, radius: number) {
  const n = width * height;
  const rows = new Uint8Array(n);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0, last = -radius - 1; x < width; x++) {
      if (edge[row + x]) last = x;
      if (x - last <= radius) rows[row + x] = 1;
    }
    for (let x = width - 1, next = width + radius; x >= 0; x--) {
      if (edge[row + x]) next = x;
      if (next - x <= radius) rows[row + x] = 1;
    }
  }
  // the column pass walks row by row, keeping the last/next dilated row per column, to stay cache friendly
  const boundary = new Uint8Array(n);
  const last = new Int32Array(width).fill(-radius - 1);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (rows[y * width + x]) last[x] = y;
      if (y - last[x] <= radius) boundary[y * width + x] = 1;
    }
  }
  const next = new Int32Array(width).fill(height + radius);
  for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x++) {
      if (rows[y * width + x]) next[x] = y;
      if (next[x] - y <= radius) boundary[y * width + x] = 1;
    }
  }
  return boundary;
}

// a flat pixel shares its label and (almost) its color with all 8 neighbours,
// so it is part of an area rather than a blend between areas
function isFlat(data: Uint8ClampedArray, labels: Int8Array, width: number, height: number, p: number) {
  const x = p % width;
  const y = (p - x) / width;
  if (x === 0 || y === 0 || x === width - 1 || y === height - 1) return false;
  const label = labels[p];
  if (label === LABEL_TRANSPARENT) return false;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const q = p + dy * width + dx;
      if (
        labels[q] !== label ||
        Math.abs(data[q * 4] - data[p * 4]) > FLAT_TOL ||
        Math.abs(data[q * 4 + 1] - data[p * 4 + 1]) > FLAT_TOL ||
        Math.abs(data[q * 4 + 2] - data[p * 4 + 2]) > FLAT_TOL
      ) {
        return false;
      }
    }
  }
  return true;
}

// neutral + palette colors (labels are bits of a byte) + fixed colors
const MAX_ANCHORS = 1 + 8 + MAX_FIXED_ANCHORS;
// per anchor: chroma (color minus its mean) of the template color, then what it becomes relative to that mean
const ANCHOR_FIELDS = 6;

// `strayWidth` > 0 absorbs palette features up to that many px wide into the area around them (see absorbStrayLabels)
export function recolorPixels(data: Uint8ClampedArray, width: number, height: number, colors: ColorsOption, strayWidth: number = 0) {
  const n = width * height;
  if (!data || data.length < n * 4) return;

  const original = flattenColors(colors.original);
  const current = flattenColors(colors.current);
  const labels = labelPixels(data, width, height, original);
  if (strayWidth > 0) absorbStrayLabels(labels, width, height, Math.ceil(strayWidth / 2));
  const boundary = dilate(findEdges(labels, width, height), width, height, RADIUS);

  const paletteAnchors = new Float64Array((original.length / 3) * ANCHOR_FIELDS);
  for (let c = 0; c < original.length / 3; c++) {
    const mean = (original[c * 3] + original[c * 3 + 1] + original[c * 3 + 2]) / 3;
    for (let k = 0; k < 3; k++) {
      paletteAnchors[c * ANCHOR_FIELDS + k] = original[c * 3 + k] - mean;
      paletteAnchors[c * ANCHOR_FIELDS + 3 + k] = current[c * 3 + k] - mean;
    }
  }

  // 0: not computed yet, 1: flat, 2: not flat
  const flat = new Uint8Array(n);
  const anchors = new Float64Array(MAX_ANCHORS * ANCHOR_FIELDS);
  const anchorIsPalette = new Uint8Array(MAX_ANCHORS);

  // Treats a boundary pixel as a blend of the flat colors around it, and writes the same blend
  // of their replacement colors to `out`. Returns false if it doesn't look like such a blend.
  // Blends are solved on the chroma plane (color minus its mean), so the brightness offset
  // of the pixel is kept the same way the legacy recolor keeps shading.
  const blendRecolor = (p: number, out: Uint8ClampedArray, o: number) => {
    const x = p % width;
    const y = (p - x) / width;

    // the neutral anchor stands for blends with black, white or grey
    for (let f = 0; f < ANCHOR_FIELDS; f++) anchors[f] = 0;
    anchorIsPalette[0] = 0;
    let count = 1;
    let usedPalette = 0;
    let fixedCount = 0;

    for (let yy = Math.max(0, y - RADIUS); yy <= Math.min(height - 1, y + RADIUS); yy++) {
      for (let xx = Math.max(0, x - RADIUS); xx <= Math.min(width - 1, x + RADIUS); xx++) {
        const q = yy * width + xx;
        if (!flat[q]) flat[q] = isFlat(data, labels, width, height, q) ? 1 : 2;
        if (flat[q] !== 1) continue;
        const label = labels[q];
        if (label >= 0) {
          if (usedPalette & (1 << label)) continue;
          usedPalette |= 1 << label;
          for (let f = 0; f < ANCHOR_FIELDS; f++) anchors[count * ANCHOR_FIELDS + f] = paletteAnchors[label * ANCHOR_FIELDS + f];
          anchorIsPalette[count++] = 1;
        } else if (fixedCount < MAX_FIXED_ANCHORS) {
          const mean = (data[q * 4] + data[q * 4 + 1] + data[q * 4 + 2]) / 3;
          const cr = data[q * 4] - mean;
          const cg = data[q * 4 + 1] - mean;
          const cb = data[q * 4 + 2] - mean;
          if (cr * cr + cg * cg + cb * cb < NEUTRAL_CHROMA) continue;
          let duplicate = false;
          for (let a = 1; a < count && !duplicate; a++) {
            const f = a * ANCHOR_FIELDS;
            duplicate = !anchorIsPalette[a] && Math.abs(anchors[f] - cr) <= 8 && Math.abs(anchors[f + 1] - cg) <= 8 && Math.abs(anchors[f + 2] - cb) <= 8;
          }
          if (duplicate) continue;
          // non palette colors keep their own color
          const f = count * ANCHOR_FIELDS;
          anchors[f] = anchors[f + 3] = cr;
          anchors[f + 1] = anchors[f + 4] = cg;
          anchors[f + 2] = anchors[f + 5] = cb;
          anchorIsPalette[count++] = 0;
          fixedCount++;
        }
      }
    }
    if (!usedPalette) return false;

    const i = p * 4;
    const mean = (data[i] + data[i + 1] + data[i + 2]) / 3;
    const pr = data[i] - mean;
    const pg = data[i + 1] - mean;
    const pb = data[i + 2] - mean;

    let bestScore = Infinity;
    let bestResidual = 0;
    let bestA = 0;
    let bestB = 0;
    let bestT = 0;
    for (let a = 0; a < count; a++) {
      const fa = a * ANCHOR_FIELDS;
      for (let b = a + 1; b < count; b++) {
        const fb = b * ANCHOR_FIELDS;
        const dr = anchors[fa] - anchors[fb];
        const dg = anchors[fa + 1] - anchors[fb + 1];
        const db = anchors[fa + 2] - anchors[fb + 2];
        const len = dr * dr + dg * dg + db * db;
        let t = len > 0 ? ((pr - anchors[fb]) * dr + (pg - anchors[fb + 1]) * dg + (pb - anchors[fb + 2]) * db) / len : 1;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const er = pr - (anchors[fb] + t * dr);
        const eg = pg - (anchors[fb + 1] + t * dg);
        const eb = pb - (anchors[fb + 2] + t * db);
        const residual = er * er + eg * eg + eb * eb;
        const score = residual + (anchorIsPalette[a] ? 0 : TIE_EPS) + (anchorIsPalette[b] ? 0 : TIE_EPS);
        if (score < bestScore) {
          bestScore = score;
          bestResidual = residual;
          bestA = a;
          bestB = b;
          bestT = t;
        }
      }
    }
    if (bestResidual > RESID_TOL) return false;

    const paletteWeight = (anchorIsPalette[bestA] ? bestT : 0) + (anchorIsPalette[bestB] ? 1 - bestT : 0);
    if (paletteWeight < 1 / 255) {
      // not related to the palette colors (e.g. an outline), keep it as is
      out[o] = data[i];
      out[o + 1] = data[i + 1];
      out[o + 2] = data[i + 2];
      return true;
    }

    const fa = bestA * ANCHOR_FIELDS;
    const fb = bestB * ANCHOR_FIELDS;
    out[o] = mean + bestT * anchors[fa + 3] + (1 - bestT) * anchors[fb + 3];
    out[o + 1] = mean + bestT * anchors[fa + 4] + (1 - bestT) * anchors[fb + 4];
    out[o + 2] = mean + bestT * anchors[fa + 5] + (1 - bestT) * anchors[fb + 5];
    return true;
  };

  // boundary pixels are solved into a separate buffer first, so every pixel reads the original neighbours
  const blended = new Uint8Array(n);
  const output = new Uint8ClampedArray(n * 3);
  for (let p = 0; p < n; p++) {
    if (!boundary[p] || labels[p] === LABEL_TRANSPARENT) continue;
    if (!flat[p]) flat[p] = isFlat(data, labels, width, height, p) ? 1 : 2;
    // flat pixels are part of an area rather than a blend, the legacy recolor is exact for them
    if (flat[p] !== 1 && blendRecolor(p, output, p * 3)) blended[p] = 1;
  }

  for (let p = 0; p < n; p++) {
    const i = p * 4;
    if (blended[p]) {
      data[i] = output[p * 3];
      data[i + 1] = output[p * 3 + 1];
      data[i + 2] = output[p * 3 + 2];
    } else {
      legacyRecolor(data, i, labels[p], original, current);
    }
    const alpha = data[i + 3];
    // fade instead of cutting off, so the garment outline stays smooth
    if (alpha < ALPHA_FADE) data[i + 3] = alpha * (alpha / ALPHA_FADE) * (alpha / ALPHA_FADE);
  }
}
